/**
 * @fileoverview Geospatial source-file detection helpers for the import run flow.
 * @module import-editor/utils/geoFileDetect
 *
 * Responsibilities:
 * - Detect geospatial import files by extension (parcel-mode auto-selection).
 * - Detect GeoParquet sources so the spatial-filter selector only appears
 *   for formats the DuckDB spatial filter can actually process.
 *
 * Dependencies:
 * - None — pure string matching.
 *
 * Architecture:
 * - Pure functions shared by the unified run flow. Previously lived inline
 *   in tenant-web's ImportWizardContainer; extracted so the shared run flow
 *   (and any future host) cannot drift on what counts as a geo file.
 *
 * Notes:
 * - `.zip` counts as geo because shapefiles and file geodatabases ship as zips.
 * - Spatial filtering applies ONLY to GeoParquet — other geo formats are
 *   converted to GeoParquet during upload, but the selector must not appear
 *   for the raw formats before conversion resolves `sourceFormat`.
 */

/**
 * @reusable isGeoFile
 * Usage:        Run flow upload step — auto-select parcel mode for geo files.
 * Rules:        Pure; extension-based; unknown extensions return false.
 *
 * Detect whether a filename is a supported geospatial import format.
 */
export function isGeoFile(filename: string): boolean {
  const normalized = filename.toLowerCase();
  return (
    normalized.endsWith('.gpkg')
    || normalized.endsWith('.gdb')
    || normalized.endsWith('.geojson')
    || normalized.endsWith('.zip')
  );
}

/**
 * @reusable isGeoParquetSource
 * Usage:        Run flow upload step — gate the spatial-filter selector.
 * Rules:        Pure. Checks converted fileType first, then sourceFormat,
 *               then falls back to the .parquet extension so the selector
 *               appears immediately on file pick.
 *
 * Detect whether the current source is GeoParquet (the only format the
 * spatial filter applies to).
 */
export function isGeoParquetSource(
  filename: string,
  sourceFormat: string | null | undefined,
  fileType?: string | null,
): boolean {
  if (fileType && fileType === 'geoparquet') return true;
  if (sourceFormat === 'geoparquet') return true;
  return /\.parquet$/i.test(filename);
}
